import api from './api/config/api';
import type {
    CreatePromptRequest,
    UpdatePromptRequest,
    UpdateWorkflowDefinitionRequest,
    CustomizePageResponse,
    Prompt,
    Workflow,
} from '../types/superAdminDataEntry.type';

/**
 * Fetches all data needed for the customize page including prompts, tenants, and agent configs
 * @returns {Promise<CustomizePageResponse>} The customize page data
 */
const getCustomizePage = async (): Promise<CustomizePageResponse> => {
    const response = await api.get<CustomizePageResponse>('/customize');
    return response.data;
};

/**
 * Creates a new prompt
 * @param {CreatePromptRequest} promptData - The prompt data to create
 * @returns {Promise<Prompt>} The created prompt
 */
const createPrompt = async (promptData: CreatePromptRequest): Promise<Prompt> => {
    const response = await api.post<Prompt>('/customize/prompt', promptData);
    return response.data;
};

/**
 * Updates an existing prompt
 * @param {string} promptId - The ID of the prompt to update
 * @param {UpdatePromptRequest} promptData - The updated prompt data
 * @returns {Promise<Prompt>} The updated prompt
 */
const updatePrompt = async (promptId: string, promptData: UpdatePromptRequest): Promise<Prompt> => {
    const response = await api.put<Prompt>(`/customize/prompt/${promptId}`, promptData);
    return response.data;
};

/**
 * Updates a workflow definition
 * @param {UpdateWorkflowDefinitionRequest} data - The workflow definition data
 * @returns {Promise<Workflow>} The updated workflow
 */
const updateWorkflowDefinition = async (data: UpdateWorkflowDefinitionRequest): Promise<Workflow> => {
    const response = await api.put<Workflow>('/customize/workflow-definition', data);
    return response.data;
};

/**
 * Deletes a prompt by ID
 * @param {string} promptId - The ID of the prompt to delete
 * @returns {Promise<void>} Success status
 */
const deletePrompt = async (promptId: string): Promise<void> => {
    const response = await api.delete<void>(`/customize/prompt/${promptId}`);
    return response.data;
};

const superAdminDataEntryService = {
    getCustomizePage,
    createPrompt,
    updatePrompt,
    updateWorkflowDefinition,
    deletePrompt,
};

export default superAdminDataEntryService;
